import { CREATURE_BY_ID, MAP, structureName } from "@eldrun/content";
import { AmbienceMixer, type AmbienceInput } from "./ambience-model.ts";
import { borderEmitter, COMBAT, CombatTracker, lowpassHz, musicState, PRIORITY, soundSide, stride, surfaceAt, type MusicState } from "./audio-model.ts";
import type { AudioEngine, LoopHandle, PlayOptions } from "./engine.ts";
import type { SoundId } from "./recipes.ts";

export interface Body {
  id: string;
  x: number;
  y: number;
  z: number;
  speed: number;
  grounded: boolean;
  swimming: boolean;
}

/** O que a cena entrega ao áudio a cada quadro (tudo já calculado lá). */
export interface AudioFrame {
  now: number;
  listener: { x: number; y: number; z: number; fx: number; fy: number; fz: number; yaw: number };
  /** `onRock`: o chão é a laje de pedra da Caverna (teto suspenso). */
  self: (Body & { onBox: boolean; onRock?: boolean; vy: number }) | undefined;
  others: readonly Body[];
  /** Câmera embaixo d'água (filtro), Vida (0 a 1), caído/assistindo, Fome baixa, sem fôlego, afogando. */
  underwater: boolean;
  hp: number;
  spectating: boolean;
  lowHunger: boolean;
  exhausted: boolean;
  drowning: boolean;
  criticalHp: boolean;
  night: boolean;
  dusk: boolean;
  /** Leitos de ambiente (F46): o ouvinte sobre a terra e a noite pelo relógio (0 a 1, `nightBlend`). */
  land: boolean;
  nightBlend: number;
  /** Só em desenvolvimento (`testAmbience` dos e2e): força parte da entrada dos leitos. */
  ambience?: Partial<AmbienceInput> | undefined;
  bosses: readonly { id: string; x: number; y: number; z: number; final: boolean }[];
  challenge: boolean;
  raftStage: string | undefined;
  treta: boolean;
  cerco: { active: boolean; x: number; z: number; radius: number; closing: boolean } | undefined;
  whirlwinds: readonly { id: string; x: number; z: number }[];
  /** `state`: o estado da IA (`CREATURE_STATES`), para a trilha de combate (F84). */
  creatures: readonly { id: string; type: string; x: number; y: number; z: number; hp: number; state?: string }[];
  /** Fogo aceso (Forno, Panela, fogueira) e áreas de perigo ativas, com a identidade de cada um. */
  fires: readonly { id: string; x: number; y: number; z: number }[];
  /** Bioma no ponto (passos e leitos) e distância até a linha da costa, dos dois lados (ondas e leitos). */
  biome: (x: number, z: number) => number;
  coast: (x: number, z: number) => number;
  seed: number;
}

/** Som de cada criatura (doc 05): Corpo-Seco estala ossos, Lobisomem uiva, Matinta assobia… */
export function creatureVoice(type: string): SoundId | undefined {
  switch (type) {
    case "corpo-seco":
    case "corpo-seco-anciao":
    case "corpo-seco-colosso":
      return "corpo-seco";
    case "lobisomem":
    case "lobisomem-alfa":
      return "uivo";
    case "mula-sem-cabeca":
      return "galope";
    case "matinta-pereira":
      return "assobio-matinta";
    case "cuca":
    case "cuca-rainha":
      return "caldeirao";
    case "capelobo":
    case "capelobo-ancestral":
      return "capelobo";
    case "boitata":
      return "rugido-fogo";
    default: {
      const a = CREATURE_BY_ID.get(type)?.archetype;
      if (a === "chefe" || a === "guardiao") return "rugido";
      if (a === "assombracao") return "rosnado";
      return undefined;
    }
  }
}

/** Legenda de um som importante com a direção (doc 05, acessibilidade). */
export interface Caption {
  /** Fonte da legenda (o mesmo som contínuo só atualiza a direção). */
  key: string;
  text: string;
  until: number;
}

const CAPTIONED: Partial<Record<SoundId, string>> = {
  "assobio-matinta": "Grito da Harpia",
  uivo: "Uivo",
  rugido: "Rugido",
  "rugido-fogo": "Rugido do Dragão",
  redemoinho: "Redemoinho",
  "alarme-cerco": "O Cerco vai fechar",
  meteoro: `${structureName("pedra-fulminada")} caindo`,
  galope: "Galope",
};

/**
 * Áudio da partida: decide o que tocar a partir dos eventos e do estado de cada quadro (passos,
 * pulo e queda, água, Vida baixa, fome, fôlego, os leitos de ambiente gravados com a síntese por
 * baixo, ondas pela costa, Cerco, redemoinhos, fogo, chefes, assombrações e a trilha).
 */
export class GameAudio {
  readonly captions: Caption[] = [];
  private readonly steps = new Map<string, { x: number; z: number; acc: number }>();
  private grounded = true;
  private airborneVy = 0;
  private lastLand = 0;
  private swimming = false;
  private readonly timers = new Map<string, number>();
  private readonly loopsByKey = new Map<string, LoopHandle>();
  private wasNight: boolean | undefined;
  private wasDusk = false;
  private wasClosing = false;
  private music: MusicState = "silencio";
  private refRadius = 0;
  private listenerYaw = 0;
  private listenerPos = { x: 0, z: 0 };
  private readonly mixer = new AmbienceMixer();
  private readonly combat = new CombatTracker();

  private readonly engine: AudioEngine;
  private readonly withMusic: boolean;

  /** `music: false`: sem a trilha (a bancada `?debug=sons` simula só o ambiente). */
  constructor(engine: AudioEngine, o: { music?: boolean } = {}) {
    this.engine = engine;
    this.withMusic = o.music ?? true;
  }

  /** Efeito pontual (posição opcional), com legenda quando é importante e tem posição. */
  play(id: SoundId, o: PlayOptions = {}, now = performance.now()): void {
    this.engine.play(id, o);
    const text = CAPTIONED[id];
    if (text && o.x !== undefined && o.z !== undefined) this.caption(id, `${text} ${soundSide({ ...this.listenerPos, yaw: this.listenerYaw }, { x: o.x, z: o.z })}`, now + 3000);
  }

  /** Legenda por fonte: a mesma fonte troca o texto (direção) e o prazo. */
  caption(key: string, text: string, until: number): void {
    const same = this.captions.find((c) => c.key === key);
    if (same) {
      same.text = text;
      same.until = Math.max(same.until, until);
    } else this.captions.push({ key, text, until });
    if (this.captions.length > 4) this.captions.shift();
  }

  /** De tempos em tempos (com variação), por chave. */
  private every(key: string, now: number, min: number, max: number): boolean {
    const at = this.timers.get(key);
    if (at === undefined) {
      this.timers.set(key, now + min + Math.random() * (max - min));
      return false;
    }
    if (now < at) return false;
    this.timers.set(key, now + min + Math.random() * (max - min));
    return true;
  }

  private loop(key: string, id: SoundId, x: number | undefined, y: number, z: number, gain: number, bus: "ambiente" | "efeitos" = "ambiente"): void {
    let l = this.loopsByKey.get(key);
    if (!l) {
      l = this.engine.loop(id, bus);
      // Sem contexto ou buffer ainda: não guarda, tenta de novo no próximo quadro.
      if (!l.live) return;
      this.loopsByKey.set(key, l);
    }
    l.set(x, y, z, gain);
  }

  private keep(keys: Set<string>): void {
    for (const [key, l] of this.loopsByKey) {
      if (keys.has(key)) continue;
      l.stop(0.8);
      this.loopsByKey.delete(key);
    }
  }

  update(f: AudioFrame): void {
    const L = f.listener;
    this.listenerYaw = L.yaw;
    this.listenerPos = { x: L.x, z: L.z };
    this.engine.setListener(L.x, L.y, L.z, L.fx, L.fy, L.fz);
    this.engine.setLowpass(lowpassHz(f.hp, f.underwater, f.spectating));
    this.engine.setReverb(f.night ? 0.5 : 0.1);
    for (let i = this.captions.length - 1; i >= 0; i--) if (this.captions[i]!.until < f.now) this.captions.splice(i, 1);

    // Passos (o próprio e os outros perto), pulo, queda e mergulho.
    const bodies = f.self ? [f.self, ...f.others] : f.others;
    const seen = new Set<string>();
    for (const b of bodies) {
      seen.add(b.id);
      const s = this.steps.get(b.id) ?? { x: b.x, z: b.z, acc: 0 };
      const moved = Math.hypot(b.x - s.x, b.z - s.z);
      s.x = b.x;
      s.z = b.z;
      if (moved < 3 && b.speed > 0.5 && (b.grounded || b.swimming)) {
        s.acc += moved;
        if (s.acc >= stride(b.speed)) {
          s.acc = 0;
          const onBox = b === f.self ? f.self.onBox : false;
          const surface = surfaceAt(f.biome(b.x, b.z), onBox, b.swimming, b === f.self && f.self.onRock === true);
          const mine = b === f.self;
          this.engine.play(`passo-${surface}`, mine ? { gain: 0.5 } : { x: b.x, y: b.y, z: b.z, gain: 0.7 });
        }
      } else if (moved >= 3) s.acc = 0;
      this.steps.set(b.id, s);
    }
    for (const id of this.steps.keys()) if (!seen.has(id)) this.steps.delete(id);
    if (f.self) {
      if (this.grounded && !f.self.grounded && f.self.vy > 2) this.engine.play("pulo", { gain: 0.5 });
      if (!f.self.grounded) this.airborneVy = Math.min(this.airborneVy, f.self.vy);
      if (!this.grounded && f.self.grounded && f.now - this.lastLand > 200) {
        this.lastLand = f.now;
        if (this.airborneVy < -4) this.engine.play("aterrissa", { gain: Math.min(1, -this.airborneVy / 14) });
        this.airborneVy = 0;
      }
      if (!this.swimming && f.self.swimming) this.engine.play("mergulho", { gain: 0.6 });
      this.grounded = f.self.grounded;
      this.swimming = f.self.swimming;
    }

    // Corpo: coração com a Vida crítica, ronco de fome, respiração sem fôlego, bolhas afogando.
    if (f.criticalHp && !f.spectating && this.every("coracao", f.now, 900, 900)) this.engine.play("coracao", { bus: "ui", rate: 1, gain: 0.8 });
    if (f.lowHunger && this.every("fome", f.now, 30_000, 60_000)) this.engine.play("fome", { gain: 0.6 });
    if (f.exhausted && this.every("respira", f.now, 1300, 1300)) this.engine.play("respira", { gain: 0.5 });
    if (f.drowning && this.every("bolhas", f.now, 900, 1500)) this.engine.play("bolhas", { gain: 0.7 });

    // Relógio: Boca da Noite, anoitecer e amanhecer.
    if (f.dusk && !this.wasDusk) this.engine.play("boca-da-noite", { bus: "ui", priority: PRIORITY.importante });
    if (this.wasNight !== undefined && f.night !== this.wasNight) this.engine.play(f.night ? "ferroada-anoitecer" : "ferroada-amanhecer", { bus: "ui", priority: PRIORITY.importante });
    this.wasDusk = f.dusk;
    this.wasNight = f.night;

    const keys = new Set<string>();
    // Ambiente (F46): os leitos gravados pelo lugar, pela hora e pela Jangada; por baixo, a síntese
    // (vento sempre; pássaros de dia; grilos e sapos à noite; ondas pela costa), cada camada
    // abaixando sob o leito que a cobre enquanto ele toca (sem o leito, ela volta).
    const keep = (key: string) => keys.add(key);
    const coast = f.coast(L.x, L.z);
    const mix = this.mixer.step({ land: f.land, coast, nightBlend: f.nightBlend, raftStage: f.raftStage, underwater: f.underwater, biome: f.biome(L.x, L.z), dusk: f.dusk, ...f.ambience }, f.now);
    const { cover } = this.engine.ambience(mix);
    this.loop("vento", "vento", undefined, 0, 0, f.underwater ? 0 : cover.wind);
    keep("vento");
    this.loop("grilos", "grilos", undefined, 0, 0, f.night ? 0.35 * cover.night : 0);
    keep("grilos");
    // Pássaros e sapos cobertos de todo não tocam (nem gastam voz).
    const AUDIBLE = 0.05;
    if (!f.night && !f.underwater && cover.birds > AUDIBLE && this.every("passaro", f.now, 2500, 7000)) this.engine.play("passaro", { x: L.x + (Math.random() - 0.5) * 50, y: L.y + 8, z: L.z + (Math.random() - 0.5) * 50, priority: PRIORITY.ambiente, bus: "ambiente", gain: 0.5 * cover.birds });
    if (f.night && cover.night > AUDIBLE && this.every("sapo", f.now, 4000, 9000)) this.engine.play("sapo", { x: L.x + (Math.random() - 0.5) * 40, y: L.y, z: L.z + (Math.random() - 0.5) * 40, priority: PRIORITY.ambiente, bus: "ambiente", gain: 0.5 * cover.night });
    if (coast < 90) {
      // As ondas vêm da linha da costa mais perto (doc 05): a distância cresce para longe dela dos
      // dois lados (terra e mar), então o emissor desce pelo gradiente até a linha.
      const e = 6;
      const gx = f.coast(L.x + e, L.z) - f.coast(L.x - e, L.z);
      const gz = f.coast(L.x, L.z + e) - f.coast(L.x, L.z - e);
      const n = Math.hypot(gx, gz);
      if (n > 1e-3) {
        const d = Math.max(4, coast);
        this.loop("ondas", "ondas", L.x - (gx / n) * d, 0, L.z - (gz / n) * d, 0.5 * (1 - coast / 90) * cover.waves);
        keep("ondas");
      }
    }

    // Cerco: fogo (o loop gravado, F46) no ponto da borda mais perto (fora dele, em você) e o alarme ao começar a fechar.
    const c = f.cerco;
    if (c?.active && c.radius > 0.5 && f.self) {
      const p = borderEmitter(c, c.radius, f.self);
      const dist = Math.hypot(p.x - f.self.x, p.z - f.self.z);
      this.loop("cerco", "fogo", p.x, f.self.y + 1, p.z, p.outside ? 0.7 : Math.max(0, 0.6 * (1 - dist / 60)), "efeitos");
      keep("cerco");
      if (c.closing && !this.wasClosing) this.play("alarme-cerco", { bus: "ui", priority: PRIORITY.critico, x: p.x, z: p.z }, f.now);
      this.refRadius = Math.max(this.refRadius, c.radius);
    }
    this.wasClosing = c?.closing === true;

    // Redemoinhos a menos de 60 m (os 3 mais perto) e fogos acesos perto (os 3 mais perto).
    const near = <T extends { x: number; z: number }>(list: readonly T[], r: number, n: number) =>
      list
        .map((w) => ({ w, d: Math.hypot(w.x - L.x, w.z - L.z) }))
        .filter((e) => e.d < r)
        .sort((a, b) => a.d - b.d)
        .slice(0, n);
    near(f.whirlwinds, MAP.whirlwinds, 3).forEach(({ w }) => {
      // Por identidade: a legenda acompanha o mesmo redemoinho e a direção gira com a câmera.
      const key = `redemoinho-${w.id}`;
      this.caption(key, `Redemoinho ${soundSide({ ...this.listenerPos, yaw: L.yaw }, w)}`, f.now + 1000);
      this.loop(key, "redemoinho", w.x, L.y, w.z, 0.6, "efeitos");
      keep(key);
    });
    // Fogo por identidade: andar não troca o loop de um fogo para outro (o emissor não pula).
    near(f.fires, 25, 3).forEach(({ w }) => {
      const key = `fogo-${w.id}`;
      this.loop(key, "fogo", w.x, w.y, w.z, 0.35);
      keep(key);
    });
    // Chefes: drone grave onde estão (até 100 m).
    f.bosses.forEach((b, i) => {
      if (Math.hypot(b.x - L.x, b.z - L.z) > 100) return;
      this.loop(`chefe-${i}`, "drone-chefe", b.x, b.y, b.z, 0.5, "efeitos");
      keep(`chefe-${i}`);
    });
    this.keep(keys);

    // Assombrações perto resmungam de tempos em tempos; contam para a intensidade da noite. Uma
    // alerta, perseguindo ou atacando a até 30 m do corpo (da câmera, assistindo) é combate.
    let haunts = 0;
    let threat = false;
    const me = f.self ?? L;
    for (const cr of f.creatures) {
      if (cr.hp <= 0) continue;
      const d = Math.hypot(cr.x - L.x, cr.z - L.z);
      const arch = CREATURE_BY_ID.get(cr.type)?.archetype;
      if (arch === "neutra" || arch === undefined) continue;
      if (!threat && cr.state !== undefined && COMBAT.states.includes(cr.state) && Math.hypot(cr.x - me.x, cr.z - me.z) <= COMBAT.radius) threat = true;
      if (d > 40) continue;
      haunts++;
      const voice = creatureVoice(cr.type);
      if (voice && this.every(`voz-${cr.id}`, f.now, 6000, 14_000)) this.play(voice, { x: cr.x, y: cr.y + 1, z: cr.z, priority: arch === "assombracao" ? PRIORITY.comum : PRIORITY.importante }, f.now);
    }

    // Trilha.
    const intensity = c?.active && this.refRadius > 0 ? 1 - c.radius / this.refRadius : f.bosses.length ? 0.6 : Math.min(1, haunts / 4);
    this.music = musicState({ screen: "jogando", night: f.night, dusk: f.dusk, boss: f.bosses.length > 0, challenge: f.challenge, raftStage: f.raftStage, treta: f.treta, cercoClosing: c?.closing === true || (c?.active === true && this.refRadius > 0 && c.radius < this.refRadius * 0.5), spectating: f.spectating, combat: this.combat.update(f.now, threat) });
    if (this.withMusic) this.engine.setMusic(this.music, intensity, f.seed);
  }

  get musicState(): MusicState {
    return this.music;
  }

  dispose(): void {
    this.loopsByKey.clear();
    this.combat.reset();
    this.engine.stopScene();
    this.engine.resetMix();
  }
}
